import type { Prisma } from "@prisma/client";
import { idSchema } from "@reactive-resume/schema";
import { dateSchema } from "@reactive-resume/utils";
import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

import { secretsSchema } from "../secrets";
import { subscriptionSchema } from "../subscription";
import { usageLimitSchema } from "../usage-limit";

export const usernameSchema = z
  .string()
  .min(3)
  .max(255)
  .regex(/^[\w.-]+$/, {
    message: "Usernames can only contain letters, numbers, periods, hyphens, and underscores.",
  })
  .transform((value) => value.toLowerCase());

export const userSchema = z.object({
  id: idSchema,
  name: z.string().min(1).max(255),
  picture: z.literal("").or(z.null()).or(z.string().url()),
  username: usernameSchema,
  email: z
    .string()
    .email()
    .transform((value) => value.toLowerCase()),
  locale: z.string().default("en-US"),
  emailVerified: z.boolean().default(false),
  twoFactorEnabled: z.boolean().default(false),
  isAdmin: z.boolean().default(false),
  deletionScheduledAt: dateSchema.nullable().default(null),
  // Soft-delete tombstone (never-delete policy, 2026-08-07). Always null for any
  // user who can reach an authenticated route — UserService filters deleted
  // accounts out — but present on the type because Prisma User rows flow
  // straight into this DTO in the OAuth strategies.
  deletedAt: dateSchema.nullable().default(null),
  provider: z.enum(["email", "github", "google", "openid"]).default("email"),
  createdAt: dateSchema,
  updatedAt: dateSchema,
  settings: z.custom<Prisma.JsonValue>().nullable(),
  onboardingCompleted: z.boolean().default(false),
  onboardingStep: z.string().nullable().default(null),
  // Day-2 activation email stamp (lifecycle) — server-only bookkeeping.
  activationEmailSentAt: dateSchema.nullable().default(null),
  // Job-profile backstop attempt bookkeeping — server-only, never read by the
  // client. Present on the type for the same reason as `deletedAt`: Prisma User
  // rows flow straight into this DTO in the OAuth strategies.
  profileBackstopAttempts: z.number().int().default(0),
  profileBackstopLastAt: dateSchema.nullable().default(null),
  // Marketing-email opt-out stamp. NULL = subscribed. Exposed to the client so
  // settings can render the current state; transactional mail ignores it.
  lifecycleEmailsOptOutAt: dateSchema.nullable().default(null),
  // Spec 031 — per-user board toggles + launch-banner dismissal state.
  boardPreferences: z.custom<Prisma.JsonValue>().default({}),
  // Pre-launch — per-board auto-apply preferences (separate from
  // discovery toggles in boardPreferences). Empty {} means
  // "auto-apply on every supported board the user has discovery on."
  autoApplyBoardPreferences: z.custom<Prisma.JsonValue>().default({}),
  bannerDismissedAt: dateSchema.nullable().default(null),
  subscription: subscriptionSchema.nullable().default(null),
  usageLimit: usageLimitSchema.nullable().default(null),
  // Referral program (nullable for users who signed up before this was added
  // and for users who weren't referred by anyone).
  referrerId: z.string().nullable().default(null),
  referralCreditsPct: z.number().int().min(0).max(100).default(0),
});

export class UserDto extends createZodDto(userSchema) {}

export const userWithSecretsSchema = userSchema.merge(
  z.object({ secrets: secretsSchema.nullable().default(null) }),
);

export class UserWithSecrets extends createZodDto(userWithSecretsSchema) {}
